import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
import AuthGuard from './components/AuthGuard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import EmailVerification from './pages/EmailVerification';
import EmailVerified from './pages/EmailVerified';
import ResendVerification from './pages/ResendVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Editor from './pages/Editor';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import UserDetails from './pages/UserDetails';
import SubscriptionPlans from './pages/SubscriptionPlans';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import Analytics from './pages/Analytics';
import AdminAIDashboard from './pages/AdminAIDashboard';
import PublishingSettings from './pages/PublishingSettings';
import LandingPage from './pages/LandingPage';
import './App.css';

// Utility to check if tokens are expired and clear them
function clearExpiredTokens() {
  const refreshToken = localStorage.getItem('refresh_token');
  const accessToken = localStorage.getItem('access_token');

  let shouldClear = false;

  if (refreshToken) {
    try {
      // Decode JWT without verification to check expiration
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < now) {
        console.log('Refresh token expired, clearing localStorage');
        shouldClear = true;
      }
    } catch (error) {
      console.log('Invalid refresh token format, clearing localStorage');
      shouldClear = true;
    }
  }

  if (accessToken) {
    try {
      // Check access token too
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < now) {
        console.log('Access token expired, clearing localStorage');
        shouldClear = true;
      }
    } catch (error) {
      console.log('Invalid access token format, clearing localStorage');
      shouldClear = true;
    }
  }

  if (shouldClear) {
    localStorage.clear(); // Clear everything to be sure
    sessionStorage.clear(); // Clear session storage too
    console.log('All storage cleared due to expired tokens');
  }
}



function App() {
  useEffect(() => {
    // Clear expired tokens on app startup
    clearExpiredTokens();
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/" element={<AuthGuard requireAuth={false}><LandingPage /></AuthGuard>} />
            <Route path="/login" element={<AuthGuard requireAuth={false}><Login /></AuthGuard>} />
            <Route path="/signup" element={<AuthGuard requireAuth={false}><Signup /></AuthGuard>} />
            <Route path="/verify-email" element={<AuthGuard requireAuth={false}><EmailVerification /></AuthGuard>} />
            <Route path="/email-verified" element={<AuthGuard requireAuth={false}><EmailVerified /></AuthGuard>} />
            <Route path="/resend-verification" element={<AuthGuard requireAuth={false}><ResendVerification /></AuthGuard>} />
            <Route path="/forgot-password" element={<AuthGuard requireAuth={false}><ForgotPassword /></AuthGuard>} />
            <Route path="/reset-password" element={<AuthGuard requireAuth={false}><ResetPassword /></AuthGuard>} />
            <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
            <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
            <Route path="/subscription" element={<AuthGuard><Subscription /></AuthGuard>} />
            <Route path="/subscription/success" element={<AuthGuard><SubscriptionSuccess /></AuthGuard>} />
            <Route path="/subscription/cancel" element={<AuthGuard><SubscriptionCancel /></AuthGuard>} />
            <Route path="/memoir/:memoirId" element={<AuthGuard><Editor /></AuthGuard>} />
            <Route path="/memoir/:memoirId/chapter/:chapterId" element={<AuthGuard><Editor /></AuthGuard>} />
            <Route path="/memoir/:memoirId/settings/publishing" element={<AuthGuard><PublishingSettings /></AuthGuard>} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AuthGuard><AdminDashboard /></AuthGuard>} />
            <Route path="/admin/users" element={<AuthGuard><UserManagement /></AuthGuard>} />
            <Route path="/admin/users/:userId" element={<AuthGuard><UserDetails /></AuthGuard>} />
            <Route path="/admin/plans" element={<AuthGuard><SubscriptionPlans /></AuthGuard>} />
            <Route path="/admin/analytics" element={<AuthGuard><Analytics /></AuthGuard>} />
            <Route path="/admin/ai" element={<AuthGuard><AdminAIDashboard /></AuthGuard>} />
          </Routes>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
